import { useRouter as useDefaultRouter } from "@/i18n/routing";
import { useParams } from "next/navigation";

type RouterOptions = {
  locale?: string;
  scroll?: boolean;
};

type NavigateOptions = RouterOptions & {
  replace?: boolean;
  shallow?: boolean;
};

export const useRouter = () => {
  const router = useDefaultRouter();
  const params = useParams();
  const store = params.store as string;

  const ensureStorePath = (path: string): string => {
    // Remove leading slash if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;

    // If path already includes store, return as is
    if (cleanPath.startsWith(`${store}/`)) {
      return `/${cleanPath}`;
    }

    // Handle empty or root path
    if (!cleanPath) {
      return `/${store}`;
    }

    // Add store prefix
    return `/${store}/${cleanPath}`;
  };

  const push = (path: string, options?: NavigateOptions) => {
    const storePrefixedPath = ensureStorePath(path);
    return router.push(storePrefixedPath, options);
  };

  const replace = (path: string, options?: NavigateOptions) => {
    const storePrefixedPath = ensureStorePath(path);
    return router.replace(storePrefixedPath, options);
  };

  const prefetch = (path: string, options?: NavigateOptions) => {
    const storePrefixedPath = ensureStorePath(path);
    return router.prefetch(storePrefixedPath, options);
  };

  return {
    ...router,
    push,
    replace,
    prefetch,
  };
};
